// Implements: F-UIX-025, F-GEN-033, F-GEN-034, F-UIX-008, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * The two diagrams of ONE version (mockup v3 P6): the two tabs, the pane of the chosen one, and
 * the consent to a paid draw.
 *
 * Each kind has its own draw (a version holds both, each drawn once, C6.3): its state lives here
 * for both tabs, so switching tab never loses a draw under way. The page keys this container by
 * `mockupId:versionId`, so nothing (a refusal, a typed brief, a draw) leaks into another version.
 *
 * Only the walkthrough is quoted (that is the step F-GEN-034 makes paid and optional): the hook
 * does not reach `running` for it until the person has confirmed the quote in `ConfirmDialog`.
 * That dialog shows the ceiling of a charge the person is asked to consent to (OD-12, F-UIX-025).
 * The drawn line shows what the stored diagram cost and when it was drawn ("hoy a las 10:24 ·
 * USD 0.02", v3), from the version's costs (C7.1), only to whoever may edit the mockup (P7-1):
 * the other money of this view, named in the OD-12 note beside the version cost places.
 *
 * A refusal to draw (a `409` with its public reason, P6-B2) is shown in its own words and switches
 * the draw off: pressing again cannot change it. So is the monthly cap (a `403 USAGE_LIMIT_REACHED`,
 * v3 `cap_reached`): the same "No se dibujó." notice, with the sentence of its reason and renewal. A rate limit is a wait (UIX.2); any other failure
 * is an error with a way back to the form that never re-sends the paid call by itself.
 *
 * The journey's legend shows only the marks its diagram uses (owner 2026-10-19, 6A): the drawn
 * frame posts its Mermaid source (`useDiagramSource`, keyed by the frame path) and `legendKeysOf`
 * reads the start class and the dotted edges from it. Until it posts, or for a diagram without the
 * marks, the legend keeps "Pantalla" and "Flecha".
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey, versionTreeKey } from "../../../shared/api/queryKeys";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useEditorVersionCosts } from "../../../shared/api/versionCosts";
import { fetchVersionNodes } from "../../../shared/api/versionTree";
import { useTranslation } from "../../../shared/i18n";
import { previewDiagramDocumentPath, previewWalkthroughDocumentPath } from "../../../shared/ui/frameDocumentPath";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { USAGE_LIMIT_REACHED, usageRefusalText } from "../../../shared/ui/molecules/usageRefusal";
import { costAmountText } from "../../../shared/ui/costText";
import { moneyText } from "../../../shared/ui/moneyText";
import type { ArtifactRef, MockupDetailView, VersionTreeNode } from "../../../types/api";
import type { DiagramKind } from "../../../types/diagrams";
import { refusalOf } from "../api/diagramsApi";
import { DiagramPane } from "../components/DiagramPane";
import type { DrawnDiagram } from "../components/DiagramPane";
import { DiagramTabs, diagramTabPanelProps } from "../components/DiagramTabs";
import { useDiagram } from "../hooks/useDiagram";
import { useDiagramSource } from "../hooks/useDiagramSource";
import { useDiagramDownload } from "./useDiagramDownload";
import type { UseDiagramResult } from "../hooks/useDiagram";
import { legendKeysOf, NO_MARKS } from "../model/legendKeys";
import { diagramStateOf } from "../model/diagramState";
import type { DiagramPaneState } from "../model/diagramState";
import { drawnWhen } from "../model/drawnWhen";

export interface DiagramContainerProps {
  readonly mockupId: string;
  readonly versionId: string;
  /** The number of the version (`v7`). */
  readonly versionNumber: number;
  /** The pieces the version holds: which of its diagrams are drawn. */
  readonly artifacts: readonly ArtifactRef[];
  /** Only the mockup's current version is drawn (P6-1); an older one is shown read-only. */
  readonly drawable: boolean;
  /** The mockup's brief, prefilled into the draw (P6-2); `undefined` while it is read. */
  readonly defaultBrief?: string | undefined;
  readonly tab: DiagramKind;
  readonly onTabChange: (kind: DiagramKind) => void;
  /** Today, for the drawn line's "hoy a las"; injected so no test reads the wall clock (checklist 16). */
  readonly clock?: (() => Date) | undefined;
  /**
   * v3 "Compartir": the page opens its share control on the diagram of `versionId` (a module may
   * not import `sharing`). Offered only to whoever the server lets share (`may_share`).
   */
  readonly onShare?: ((versionId: string) => void) | undefined;
}

const TABS_ID = "diagram-kind";
const NO_IDS: readonly string[] = [];
const systemClock = (): Date => new Date();

function paneState(draw: UseDiagramResult, artifacts: readonly ArtifactRef[], kind: DiagramKind): DiagramPaneState {
  if (draw.phase === "done") return "ready";
  return diagramStateOf(artifacts, kind, draw.phase === "running" ? "drawing" : "idle");
}

export function DiagramContainer({
  mockupId,
  versionId,
  versionNumber,
  artifacts,
  drawable,
  defaultBrief,
  tab,
  onTabChange,
  clock = systemClock,
  onShare,
}: DiagramContainerProps): ReactElement {
  const { t } = useTranslation();
  // The shared read of the mockup (`may_share`, F-SHR-005): the one decision point of sharing.
  const mockup = useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });
  const share =
    onShare === undefined || mockup.data?.may_share !== true
      ? undefined
      : () => {
          onShare(versionId);
        };
  // The drawn line (v3 P6, P7-1): the chip's very version-costs request (every version of the
  // tree, the shared keys), asked only for whoever may edit the mockup; nobody else sees a cost.
  const nodes = useQuery<readonly VersionTreeNode[], ApiError>({
    queryKey: versionTreeKey(mockupId),
    queryFn: () => fetchVersionNodes(mockupId),
  });
  const ids = useMemo(() => nodes.data?.map((node) => node.id) ?? NO_IDS, [nodes.data]);
  const costs = useEditorVersionCosts(mockupId, ids);
  const pack = useDiagramDownload(mockupId, versionId, versionNumber);
  const journey = useDiagram(mockupId, versionId, "journey");
  const walkthrough = useDiagram(mockupId, versionId, "walkthrough");
  // `null` until the person types: until then the field shows the mockup's brief (P6-2).
  const [typed, setTyped] = useState<string | null>(null);
  const brief = typed ?? defaultBrief ?? "";
  const draw = tab === "journey" ? journey : walkthrough;
  const states = { journey: paneState(journey, artifacts, "journey"), walkthrough: paneState(walkthrough, artifacts, "walkthrough") };
  // A refusal stands only while what it was about stands (adjusted while rendering, not in an
  // effect, so the stale sentence is never painted): the walkthrough's `journey_required` lifts
  // once the journey is drawn, and `diagram_in_progress` lifts once the version is read again.
  const [readPieces, setReadPieces] = useState(artifacts);
  if (readPieces !== artifacts) {
    setReadPieces(artifacts);
    for (const kind of [journey, walkthrough]) {
      if (refusalOf(kind.error) === "diagram_in_progress") kind.dismiss();
    }
  }
  if (states.journey === "ready" && refusalOf(walkthrough.error) === "journey_required") walkthrough.dismiss();
  const refusal = refusalOf(draw.error);
  // UIX.2: a `429` is a wait, not a failure; nothing is sent again by itself (checklist 13).
  const waitSeconds = useRateLimitWait(draw.error);
  // v3 `cap_reached`: a usage refusal is a "No se dibujó." notice in the pane, never a generic error.
  const usageRefusal =
    draw.error?.code === USAGE_LIMIT_REACHED ? usageRefusalText(draw.error.reason, draw.error.renewsAt) : null;
  const failure =
    draw.phase === "failed" && refusal === null && usageRefusal === null && draw.error?.status !== 429
      ? draw.error
      : null;
  const diagramCost = costs.costs?.get(versionId)?.diagrams[tab] ?? null;
  const when = diagramCost === null ? null : drawnWhen(diagramCost.drawn_at, clock());
  const drawn: DrawnDiagram | null =
    diagramCost === null || when === null ? null : { when, cost: costAmountText(diagramCost.cost) };
  const frameSrc =
    tab === "journey"
      ? previewDiagramDocumentPath(mockupId, versionId)
      : previewWalkthroughDocumentPath(mockupId, versionId);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const source = useDiagramSource(frameRef, frameSrc);
  const legend = useMemo(() => (source === null ? NO_MARKS : legendKeysOf(source)), [source]);

  return (
    <>
      <DiagramTabs idBase={TABS_ID} selected={tab} states={states} onSelect={onTabChange} />
      <div {...diagramTabPanelProps(TABS_ID, tab)}>
        <DiagramPane
          kind={tab}
          state={states[tab]}
          versionNumber={versionNumber}
          frameSrc={frameSrc}
          drawable={drawable}
          brief={brief}
          onBriefChange={setTyped}
          onDraw={() => {
            draw.submit(brief.trim());
          }}
          busy={draw.phase === "estimating" || draw.phase === "running" || draw.phase === "confirming"}
          paused={waitSeconds !== null}
          refusal={refusal}
          usageRefusal={usageRefusal}
          onGoToJourney={() => {
            onTabChange("journey");
          }}
          announce={draw.phase === "done"}
          drawn={drawn}
          onDownload={pack.download}
          downloading={pack.pending}
          onShare={share}
          legend={legend}
          frameRef={frameRef}
        >
          {draw.phase === "estimating" ? <LoadingState label={t("diagram.estimating")} /> : null}
          {waitSeconds === null ? null : <RateLimitWait seconds={waitSeconds} />}
          {failure === null ? null : (
            <ErrorState error={failure} onRetry={failure.status === 409 ? undefined : draw.retry} />
          )}
        </DiagramPane>
      </div>
      <ConfirmDialog
        open={walkthrough.phase === "confirming"}
        title={t("diagram.confirm.title")}
        body={
          walkthrough.estimate === null
            ? ""
            : t("diagram.confirm.body", {
                cost: moneyText(walkthrough.estimate.cost.amount_micros, walkthrough.estimate.cost.currency),
              })
        }
        confirmLabel={t("common.action.confirm")}
        cancelLabel={t("common.action.cancel")}
        onConfirm={walkthrough.confirm}
        onCancel={walkthrough.dismiss}
      />
    </>
  );
}
